import { useEffect, useMemo, useState } from "react";
import type { AxiosRequestConfig } from "axios";
import { useTranslation } from "react-i18next";
import type { SWRConfiguration } from "swr";
import useSWR, { useSWRConfig } from "swr";

import * as api from "./api";
import { EventType } from "./api";
import { DEFAULT_ATTRIBUTES_SWR_CONFIG } from "./constants";
import parseLinkHeader from "./parse-link-header";
import { hasPathTraversal } from "./utils";

export interface UseAPIState<Data extends object> {
  data?: Data;
  headers: api.APIHeaders;
  isLoading: boolean;
  error?: api.APIErrorResponse;
  requestKey: string;
}

// The fetcher is typed for this hook's Data, so a fetcher built for one resource fits as well as the generic ones.
export const useAPI = <Args, Data extends object>(
  uriFunc: (args: Args) => string | null,
  args: Args,
  swrOptions?: SWRConfiguration,
  fetcher: (
    path: string,
    requestConfig?: AxiosRequestConfig
  ) => Promise<api.APISuccessResponse<Data>> = api.getFetcher
): UseAPIState<Data> => {
  const { t } = useTranslation();

  let path: string | null = uriFunc(args);

  const isInsecureRequest = hasPathTraversal(args);

  if (isInsecureRequest) {
    console.warn(
      "Path traversal detected in url params, do not make a request."
    );
    path = null;
  }

  const {
    data: response,
    error,
    isLoading,
  } = useSWR<api.APISuccessResponse<Data>, api.APIErrorResponse>(
    path,
    fetcher,
    swrOptions
  );
  const data = response?.data;
  const headers = response?.headers || {};

  const key = path || "";

  if (isInsecureRequest) {
    return {
      isLoading: false,
      headers,
      error: {
        message: t("common:api_error.insecure_parameters"),
        code: "400",
      },
      requestKey: key,
    };
  }

  return { isLoading, headers, data, error, requestKey: key };
};

export const usePostAPI = <Args, Data extends object>(
  uriFunc: (args: Args) => string,
  args: Args,
  body: Object,
  swrOptions?: SWRConfiguration,
  fetcher = api.postFetcher
): UseAPIState<Data> => {
  const { t } = useTranslation();

  let path: string | null = uriFunc(args);

  const isInsecureRequest = hasPathTraversal(args);

  if (isInsecureRequest) {
    console.warn(
      "Path traversal detected in url params, do not make a request."
    );
    path = null;
  }

  const key = path ? `${path}_${JSON.stringify(body)}` : "";

  const {
    data: response,
    error,
    isLoading,
  } = useSWR<api.APISuccessResponse<Data>, api.APIErrorResponse>(
    key,
    () => fetcher(path!, body),
    swrOptions
  );
  const { data, headers = {} } = response || {};

  if (isInsecureRequest) {
    return {
      isLoading: false,
      headers,
      error: {
        message: t("common:api_error.insecure_parameters"),
        code: "400",
      },
      requestKey: key,
    };
  }

  return { isLoading, headers, data, error, requestKey: key };
};

export interface UsePaginatedAPIState<
  Data extends object,
> extends UseAPIState<Data> {
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  // The request URL without its cursor: it changes when the query (filters, sort, search) changes, not when you page.
  resetKey: string | null;
}

interface PageCursors extends Pick<
  api.APIPaginatedRequest,
  "before" | "after"
> {
  resetKey: string | null;
}

export const usePaginatedAPI = <
  Args extends api.APIPaginatedRequest,
  Data extends object,
>(
  uriFunc: (args: Args) => string | null,
  args: Args,
  swrOptions?: SWRConfiguration,
  fetcher = api.getFetcher
): UsePaginatedAPIState<Data> => {
  const resetKey = uriFunc({ ...args, before: undefined, after: undefined });

  // Reset during render, not in an effect: SWR sends the request before effects run, so an effect reset would
  // send one request with the new query and the old cursors.
  const [cursors, setCursors] = useState<PageCursors>({
    resetKey,
    before: args.before,
    after: args.after,
  });
  if (cursors.resetKey !== resetKey) setCursors({ resetKey });

  const { before, after } = cursors;
  const apiState = useAPI<Args, Data>(
    uriFunc,
    { ...args, before, after },
    swrOptions,
    fetcher
  );

  const links = parseLinkHeader(apiState.headers.link);

  const hasPreviousPage = Boolean(links?.prev);
  const hasNextPage = Boolean(links?.next);

  const onPreviousPage = () => {
    if (!hasPreviousPage) return;

    setCursors({
      resetKey,
      before: links?.prev.before,
      after: links?.prev.after,
    });
  };

  const onNextPage = () => {
    if (!hasNextPage) return;

    setCursors({
      resetKey,
      before: links?.next.before,
      after: links?.next.after,
    });
  };

  return {
    ...apiState,
    hasPreviousPage,
    hasNextPage,
    onPreviousPage,
    onNextPage,
    resetKey,
  };
};

export interface UseLoadMoreState<T> {
  data: T[];
  hasMore: boolean;
  // True only for the first page (blocking); later "load more" pages append while existing rows stay.
  isLoadingInitial: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  // Refetches the page currently held by the cursor. A failed page carries no Link header, so it
  // leaves `hasMore` false and `loadMore` inert — this is the only way back to the rest of the list.
  retry: () => void;
  headers: api.APIHeaders;
  error?: api.APIErrorResponse;
}

// Unlike usePaginatedAPI, which swaps the visible page as the cursor moves, this hook accumulates
// every loaded page into one growing list — the model a "Show more" button needs. The cursor resets
// when the query (uriFunc(args), excluding the cursor) changes. `args` must NOT carry
// limit/before/after — those are managed here.
export const useLoadMorePages = <
  Args extends api.APIPaginatedRequest,
  T extends object,
>(
  uriFunc: (args: Args) => string | null,
  args: Args,
  initialPageSize: number,
  loadMorePageSize: number,
  fetcher: (
    path: string,
    requestConfig?: AxiosRequestConfig
  ) => Promise<api.APISuccessResponse<T[]>> = api.getFetcher
): UseLoadMoreState<T> => {
  const { mutate } = useSWRConfig();
  const baseKey = uriFunc(args) ?? "";

  // Reset during render, not in an effect: SWR sends the request before effects run, so an effect
  // reset would send one request with the new query and the old cursor, which the API rejects.
  const [cursor, setCursor] = useState<{ baseKey: string; after?: string }>({
    baseKey,
  });
  if (cursor.baseKey !== baseKey) setCursor({ baseKey });

  const { after } = cursor;

  const limit = after === undefined ? initialPageSize : loadMorePageSize;
  const apiState = useAPI<Args, T[]>(
    uriFunc,
    { ...args, limit, after },
    undefined,
    fetcher
  );

  const links = parseLinkHeader(apiState.headers.link);
  const nextCursor = links?.next?.after;

  const [accumulated, setAccumulated] = useState<{
    baseKey: string;
    order: string[];
    byKey: Record<string, T[]>;
  }>({ baseKey, order: [], byKey: {} });

  useEffect(() => {
    setAccumulated((prev) => {
      const base =
        prev.baseKey === baseKey
          ? prev
          : {
              baseKey,
              order: [] as string[],
              byKey: {} as Record<string, T[]>,
            };

      if (!apiState.data) return base;

      const order = base.order.includes(apiState.requestKey)
        ? base.order
        : [...base.order, apiState.requestKey];

      return {
        baseKey,
        order,
        byKey: { ...base.byKey, [apiState.requestKey]: apiState.data },
      };
    });
  }, [apiState.data, apiState.requestKey, baseKey]);

  // Memoize so the accumulated array keeps a stable identity between renders (otherwise the data
  // grid re-renders every row on each render, which reads as a flicker).
  const matches = accumulated.baseKey === baseKey;
  const data = useMemo(
    () =>
      matches
        ? accumulated.order.flatMap((key) => accumulated.byKey[key] ?? [])
        : [],
    [matches, accumulated.order, accumulated.byKey]
  );

  const isLoadingMore = apiState.isLoading && data.length > 0;

  return {
    data,
    // While a "load more" page is in flight the new page's Link header isn't available yet; keep
    // hasMore true so the button doesn't blink out and back during the fetch.
    hasMore: Boolean(links?.next) || isLoadingMore,
    isLoadingInitial: apiState.isLoading && data.length === 0,
    isLoadingMore,
    loadMore: () => {
      if (nextCursor) setCursor({ baseKey, after: nextCursor });
    },
    retry: () => {
      if (apiState.requestKey) void mutate(apiState.requestKey);
    },
    headers: apiState.headers,
    error: apiState.error,
  };
};

// ---------
// FLEETS
// ---------
export const useFleets = (args: api.ListFleetsRequest) =>
  usePaginatedAPI<typeof args, api.Fleet[]>(api.listFleetsRequestURI, args);

export const useListFleetsCount = (args: api.CountRequest) =>
  useAPI<typeof args, api.CountResponse>(api.listFleetCountRequestURI, args);

// ---------
// MAINTENANCE SCHEDULES
// ---------
export const useVehicleCategory = (args: api.GetApiIdRequest) =>
  useAPI<typeof args, api.VehicleGenericCategory>(
    api.getVehicleCategoryRequestURI,
    args
  );

export const useListVehicleCategories = () =>
  useAPI<null, api.VehicleGenericCategory[]>(
    api.listVehicleCategoriesRequestURI,
    null
  );

export const useListTransportCategories = () =>
  useAPI<null, api.VehicleGenericCategory[]>(
    api.listTransportCategoriesRequestURI,
    null
  );

export const useTransportCategory = (args: api.GetApiIdRequest) =>
  useAPI<typeof args, api.VehicleGenericCategory>(
    api.getTransportCategoryRequestURI,
    args
  );

export const useMaintenanceSchedule = (
  args: api.GetMaintenanceScheduleRequest
) =>
  useAPI<typeof args, api.MaintenanceSchedule[]>(
    api.listMaintenanceScheduleRequestURI,
    args
  );

// ---------
// Healthcheck
// ---------
export const useHealthcheck = () =>
  useAPI<null, api.Healthcheck>(api.getHealthcheckRequestURI, null);

export const useListLaborCodes = (args: api.ListLaborCodesRequest) =>
  usePaginatedAPI<typeof args, api.LaborCode[]>(api.listLaborCodesURI, args);

export const useListParts = (args: api.ListPartsRequest) =>
  usePaginatedAPI<typeof args, api.Part[]>(
    api.listPartsURI,
    args,
    DEFAULT_ATTRIBUTES_SWR_CONFIG
  );

export const useListECUs = (args: api.APIPaginatedRequest) =>
  useAPI<typeof args, api.ECU[]>(api.listECUsRequestURI, args);

export const useListVehiclesECUs = (args: api.APIPaginatedRequest) =>
  usePaginatedAPI<typeof args, api.VehicleECU[]>(
    api.listVehiclesECUsRequestURI,
    args
  );

export const useListOptions = (args: api.APIPaginatedRequest) =>
  useAPI<typeof args, api.Option[]>(
    api.listOptionsRequestURI,
    args,
    DEFAULT_ATTRIBUTES_SWR_CONFIG
  );

export const useListVehiclesOptions = (args: api.APIPaginatedRequest) =>
  usePaginatedAPI<typeof args, api.VehicleOption[]>(
    api.listVehiclesOptionsRequestURI,
    args
  );

export const useListVehiclesParts = (args: api.APIPaginatedRequest) =>
  usePaginatedAPI<typeof args, api.VehiclePart[]>(
    api.listVehiclesPartsRequestURI,
    args
  );

export const useEventRegistry = () =>
  useAPI<null, EventType[]>(
    api.listEventRegistryURI,
    null,
    DEFAULT_ATTRIBUTES_SWR_CONFIG
  );
